import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Heart, MessageCircle, Instagram, ExternalLink } from "lucide-react";
import { childcareData } from "../data/mock";

const Footer = () => {
  const logoUrl = "https://customer-assets.emergentagent.com/job_romantic-brahmagupta/artifacts/o69ibm0p_image.png";

  const handleWhatsAppClick = (phone, name) => {
    const message = `Hello! I'm interested in learning more about Heathbourne House Childcare. I'd like to speak with ${name}.`;
    const whatsappUrl = `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleInstagramClick = () => {
    window.open(childcareData.contact.instagram, '_blank');
  };

  return (
    <footer className="bg-slate-800 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Logo & Description */}
          <div className="lg:col-span-1">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <img 
                  src={logoUrl} 
                  alt="Heathbourne House" 
                  className="h-10 w-10 object-contain rounded-full"
                  style={{ filter: 'none' }}
                />
              </div>
              <div>
                <h3 className="text-xl font-bold">Heathbourne House</h3>
                <p className="text-blue-300">Childcare Excellence</p>
              </div>
            </div>
            <p className="text-gray-300 mb-6 text-sm">
              Nurturing young minds in a beautiful Georgian home since 2016. 
              Set on 2 acres of natural beauty in Bushey Heath.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-pink-300">Quick Links</h4>
            <div className="space-y-2">
              <Link to="/" className="block text-gray-300 hover:text-blue-300 transition-colors text-sm">
                Home
              </Link>
              <Link to="/about" className="block text-gray-300 hover:text-blue-300 transition-colors text-sm">
                About Us
              </Link>
              <Link to="/services" className="block text-gray-300 hover:text-blue-300 transition-colors text-sm">
                Services & EYFS
              </Link>
              <Link to="/funding" className="block text-gray-300 hover:text-blue-300 transition-colors text-sm">
                Government Funding
              </Link>
              <Link to="/contact" className="block text-gray-300 hover:text-blue-300 transition-colors text-sm">
                Contact Us
              </Link>
              <a 
                href="/#testimonials" 
                className="block text-pink-300 hover:text-pink-200 transition-colors text-sm font-medium"
              >
                Refer a Friend
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-pink-300">Contact Info</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-2">
                <Phone className="h-4 w-4 text-blue-300 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">
                    <strong>Jasmin:</strong> {childcareData.contact.phones.primary}
                  </p>
                  <p className="text-gray-300 text-sm">
                    <strong>Liza:</strong> {childcareData.contact.phones.secondary}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-blue-300 flex-shrink-0" />
                <a 
                  href={`mailto:${childcareData.contact.email}`}
                  className="text-gray-300 hover:text-blue-300 transition-colors text-sm break-all"
                >
                  {childcareData.contact.email}
                </a>
              </div>
              
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-blue-300 mt-0.5 flex-shrink-0" />
                <p className="text-gray-300 text-sm">{childcareData.contact.address}</p>
              </div>
            </div>
          </div>

          {/* Social & WhatsApp */}
          <div>
            <h4 className="text-lg font-semibold mb-4 text-pink-300">Connect With Us</h4>
            <div className="space-y-3">
              
              {/* Instagram */}
              <button
                onClick={handleInstagramClick}
                className="flex items-center space-x-2 text-gray-300 hover:text-pink-300 transition-colors w-full text-left"
              >
                <Instagram className="h-5 w-5" />
                <span className="text-sm">@heathbournehouse</span>
                <ExternalLink className="h-3 w-3" />
              </button>

              {/* WhatsApp Links */}
              <div className="space-y-2">
                <p className="text-sm text-gray-400">WhatsApp:</p>
                <button
                  onClick={() => handleWhatsAppClick(childcareData.contact.whatsapp.jasmin, 'Jasmin')}
                  className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-full transition-colors text-sm w-full"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Message Jasmin</span>
                </button>
                
                <button
                  onClick={() => handleWhatsAppClick(childcareData.contact.whatsapp.liza, 'Liza')}
                  className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-full transition-colors text-sm w-full"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>Message Liza</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              <p>© 2025 Heathbourne House Childcare. All rights reserved.</p>
              <p className="mt-1">Ofsted Registration: 2527526 • Food Hygiene Certified • NVQ Qualified • Paediatric First Aid</p>
            </div>
            
            <div className="flex items-center space-x-1 text-gray-400 text-sm">
              <span>Made with</span>
              <Heart className="h-4 w-4 text-red-400" />
              <span>for children's futures</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;